import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp, faSearch } from '@fortawesome/free-solid-svg-icons';

import { Dropdown, Header, Input } from '@gasbuddy/react-components';
import Loader from '../loader';
import Paginator from './paginator';
import Button from '../button';
import GenericObjectArrayProps from '../../prop-types/generic-object';

import BpUtils from '../../../lib/bputils';

import styles from './styles.css';

export default function Table({
  title,
  loading,
  data,
  tableColumns,
  paginator,
  className,
  loaderPadding,
  onPageChange,
  defaultSortColumn,
  prioritySortOrder,
  children,
  minWidth,
  onCTAClick,
  CTAText,
  onSortChange,
  filters,
  onFilterChange,
  filterLabel,
  searchable,
  pageSize: pageSizeProps,
  pageNumber: pageNumberProps,
  ...rest
}) {
  const [isLoading, setIsLoading] = React.useState(loading);
  const [pageSize, setPageSize] = React.useState(pageSizeProps || 25);
  const [pageNumber, setPageNumber] = React.useState(pageNumberProps || 1);
  const [sortOrder, setSortOrder] = React.useState(tableColumns.find(tc => tc.sortFn)?.title ? 'desc' : null);
  const [sortColumn, setSortColumn] = React.useState(defaultSortColumn || tableColumns.find(tc => tc.sortFn)?.title || null);
  const [tableData, setTableData] = React.useState(null);
  const [selectedFilter, setSelectedFilter] = React.useState(filters.length ? filters[0].name : null);
  const [searchTerm, setSearchTerm] = React.useState(null);

  React.useEffect(() => {
    const queryParams = BpUtils.getQueryParameters();
    if (queryParams?.sortOrder && queryParams?.sortColumn) {
      setSortOrder(queryParams.sortOrder);
      const normalizedSortColumn = queryParams.sortColumn.split('_').map(word => word[0].toUpperCase() + word.substr(1)).join(' ');
      setSortColumn(normalizedSortColumn);
    }
  }, []);

  React.useEffect(() => {
    setIsLoading(loading);
  }, [loading]);

  React.useEffect(() => {
    if (sortOrder) {
      BpUtils.setQueryParameter('sortOrder', sortOrder);
    }
  }, [sortOrder]);

  React.useEffect(() => {
    if (sortColumn) {
      BpUtils.setQueryParameter('sortColumn', sortColumn.toLowerCase().replace(' ', '_'));
    }
  }, [sortColumn]);

  React.useEffect(() => {
    if (sortOrder && sortColumn) {
      onSortChange({ sortOrder, sortColumn });
    }
  }, [sortOrder, sortColumn]);

  React.useEffect(() => {
    if (data) {
      if (sortOrder || sortColumn || filters.length || searchTerm) {
        sortTableData();
      } else {
        setTableData(data);
      }
    }
  }, [data, sortOrder, sortColumn, selectedFilter, searchTerm]);

  const handleOnPageChange = ({ pageSize: newPageSize, pageNumber: newPageNumber }) => {
    let pagingChanged = false;
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      pagingChanged = true;
    }
    if (newPageNumber !== pageNumber) {
      setPageNumber(newPageNumber);
      pagingChanged = true;
    }
    if (pagingChanged) {
      setIsLoading(true);
      onPageChange({ pageSize: newPageSize, pageNumber: newPageNumber });
      window.setTimeout(() => setIsLoading(false), 100);
    }
  };

  const sortTableData = () => {
    let tempTableData = data;

    if (tempTableData.length) {
      const sortedData = [];
      const columnToSort = tableColumns.find(v => v.title.toLowerCase() === sortColumn.toLowerCase());

      const { sortFn } = columnToSort;

      if (prioritySortOrder) {
        let watchedStations = tempTableData.filter(td => td.watched);
        tempTableData = tempTableData.filter(td => !td.watched);

        if (filters.length) {
          const { filterFn } = filters.find(f => f.name === selectedFilter);
          watchedStations = watchedStations.filter((d => filterFn(d)));
        }

        if (searchTerm && searchTerm.length) {
          const term = searchTerm.toLowerCase();
          watchedStations = watchedStations.filter((d => tableColumns.some((tc => (tc.value ? tc.value(d).toLowerCase().includes(term) : false)))));
        }

        sortedData.push(
          ...watchedStations.sort((a, b) => sortFn(a, b, columnToSort.value, sortOrder === 'asc')),
        );
      }

      if (filters.length) {
        const { filterFn } = filters.find(f => f.name === selectedFilter);
        tempTableData = tempTableData.filter((d => filterFn(d)));
      }

      if (searchTerm && searchTerm.length) {
        const term = searchTerm.toLowerCase();
        tempTableData = tempTableData.filter((d => tableColumns.some((tc => (tc.value ? tc.value(d).toLowerCase().includes(term) : false)))));
      }

      sortedData.push(
        ...tempTableData.sort((a, b) => sortFn(a, b, columnToSort.value, sortOrder === 'asc')),
      );

      return setTableData(sortedData);
    }
    return setTableData(data);
  };

  const toggleSortOrder = () => (setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc'));

  const handleOnSortClick = (columnTitle) => {
    if (sortColumn === columnTitle) {
      toggleSortOrder();
    } else {
      setSortColumn(columnTitle);
      setSortOrder('desc');
    }
  };

  const handleOnFilterChange = (e) => {
    setSelectedFilter(e.currentTarget.value);
    onFilterChange(e.currentTarget.value);
  };

  const handleOnSearchTermChange = (e) => {
    setSearchTerm(e.currentTarget.value);
  };

  return (
    <>
      <div className={`${styles.tableContainer} ${className}`}>
        {title ? (
          <div className={styles.tableTitle}>
            <div><Header as="h4">{title}</Header></div>
            <div className={styles.filterContainer}>
              {filters.length ? (
                <div className={styles.filterDropdownContainer}>
                  {filterLabel ? <span className={styles.filterLabel}>{filterLabel}</span> : null}
                  <div className={styles.dropdownContainer}>
                    <Dropdown
                      size="sm"
                      value={selectedFilter}
                      onChange={handleOnFilterChange}
                      options={filters.map(f => ({ label: f.name, value: f.name }))}
                    />
                  </div>
                </div>
              ) : null }
              {filters.length && searchable ? <span className={styles.spacer} /> : null}
              {searchable ? (
                <div className={styles.searchContainer}>
                  <Input size="sm" onChange={handleOnSearchTermChange} value={searchTerm} placeholder="Search Users" className={styles.searchInput} />
                  <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
                </div>
              ) : null}
            </div>
            {onCTAClick ? <div><Button primary size="sm" wide onClick={onCTAClick}>{CTAText}</Button></div> : null }
          </div>
        ) : null}
        <table className={styles.dataTable} {...rest} style={minWidth ? { minWidth: `${minWidth}px` } : {}}>
          {tableColumns.length ? (
            <thead className={styles.dataTableHeader}>
              <tr>
                {tableColumns.map(col => (
                  <th key={col.title} className={styles.tableHeaderCell} colSpan={col.colSpan || 1}>
                    <div className={styles.tableHeaderCellWrapper}>
                      <span className={styles.tableHeaderCellTitle}>{col.title}</span>
                      {col.sortFn ? (
                        <FontAwesomeIcon
                          className={`${styles.sortIcon} ${col.title === sortColumn ? styles.activeSort : null}`}
                          icon={col.title === sortColumn && sortOrder === 'asc' ? faArrowUp : faArrowDown}
                          onClick={() => handleOnSortClick(col.title)}
                        />
                      ) : null}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
          ) : null}
          <tbody className={styles.dataTableBody}>
            {isLoading ? (
              <tr>
                <td colSpan={tableColumns.reduce((total, col) => total + (col.colSpan || 1), 0)}>
                  <Loader size="md" style={{ padding: `${loaderPadding}px` }} />
                </td>
              </tr>
            ) : null}
            {/* if you need to inject custom content into the table */}
            {!isLoading && children ? children : null}
            {!isLoading && tableColumns.length && tableData?.length ? (
              tableData.slice((pageSize * (pageNumber - 1)), (pageNumber * pageSize - 1)).map(d => (
                <tr className={d.watched ? styles.watched : null}>
                  {tableColumns.map(column => (
                    column.renderTableCell ? column.renderTableCell(d) : (
                      <td
                        key={column.value}
                        colSpan={column.colSpan || 1}
                        style={column.color ? { backgroundColor: column.color(d) } : undefined}
                      >
                        {column.render ? column.render(d) : column.value(d)}
                      </td>
                    )
                  ))}
                </tr>
              ))
            ) : null }
          </tbody>
        </table>
        {paginator && !isLoading ? (
          <Paginator
            pageSize={pageSize}
            pageNumber={pageNumber}
            numberOfEntries={tableData?.length}
            onChange={handleOnPageChange}
            minWidth={minWidth}
          />
        ) : null}
      </div>
    </>
  );
}

const TableColumnPropTypes = PropTypes.shape({
  title: PropTypes.string,
  sortFn: PropTypes.oneOfType([undefined, PropTypes.func]),
  render: PropTypes.oneOfType([undefined, PropTypes.func]),
  renderTableCell: PropTypes.oneOfType([undefined, PropTypes.func]),
  value: PropTypes.func,
  color: PropTypes.oneOfType([undefined, PropTypes.string]),
  colSpan: PropTypes.oneOfType([undefined, PropTypes.number]),
});

Table.propTypes = {
  title: PropTypes.string,
  loading: PropTypes.bool,
  paginator: PropTypes.bool,
  tableColumns: PropTypes.arrayOf(TableColumnPropTypes).isRequired,
  className: PropTypes.string,
  data: GenericObjectArrayProps,
  loaderPadding: PropTypes.number,
  onPageChange: PropTypes.func,
  defaultSortColumn: PropTypes.string,
  prioritySortOrder: PropTypes.bool,
  minWidth: PropTypes.number,
  onCTAClick: PropTypes.func,
  CTAText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  onSortChange: PropTypes.func,
  filters: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    filterFn: PropTypes.func,
  })),
  onFilterChange: PropTypes.func,
  filterLabel: PropTypes.string,
  searchable: PropTypes.bool,
  pageSize: PropTypes.number,
  pageNumber: PropTypes.number,
  children: PropTypes.node,
};

Table.defaultProps = {
  title: null,
  loading: true,
  paginator: false,
  className: '',
  data: null,
  loaderPadding: 250,
  onPageChange: () => { },
  defaultSortColumn: null,
  prioritySortOrder: false,
  minWidth: null,
  onCTAClick: null,
  CTAText: null,
  onSortChange: () => { },
  filters: [],
  filterLabel: null,
  onFilterChange: () => { },
  searchable: false,
  pageSize: 25,
  pageNumber: 1,
  children: null,
};
